import {
  customDateFormatter,
  DefaultDateTimeFormatter,
  intl,
  intlFmt
} from '../../lib';
import type { icModel } from 'generated-ts-common';
import { useMemo } from 'react';

type TaskCodeType = icModel.saf.infrastructure.model.appointments.TaskCodeType;
type Task = icModel.saf.infrastructure.model.appointments.Task;
type SchedulingCode = icModel.saf.infrastructure.model.appointments.SchedulingCode;
type TaskInputCodeValueComponent = icModel.saf.infrastructure.model.appointments.TaskInputCodeValueComponent;
type TaskInOutComponent = icModel.saf.infrastructure.model.appointments.TaskInOutComponent<any>;

export enum TaskStatusEnum {
  READY = 'READY',
  INPROGRESS = 'INPROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED'
}

export function getTaskLabel(task: Task, valueCharLimit?: number): string {
  let taskLabel = '';
  if (isTaskCodeUpload(task) && task?.input[0]?.value) {
    taskLabel = intlFmt(
      'appointmentSchedulingPortlet.appointmentDetail.taskLabel.upload',
      ' "' +
        limitValueCharLengthIfNeeded(task?.input[0]?.value, valueCharLimit) +
        '"'
    );
  } else if (isTaskCodeRead(task) && task?.input[0]?.type?.displayName) {
    taskLabel = intlFmt(
      'appointmentSchedulingPortlet.appointmentDetail.taskLabel.read',
      ' "' +
        limitValueCharLengthIfNeeded(
          task?.input[0]?.type?.displayName,
          valueCharLimit
        ) +
        '"'
    );
  } else if (isTaskCodeQuestionnaire(task) && task?.questionnaire?.title) {
    taskLabel = intlFmt(
      'appointmentSchedulingPortlet.appointmentDetail.fill',
      ' "' +
        limitValueCharLengthIfNeeded(
          task?.questionnaire?.title,
          valueCharLimit
        ) +
        '"'
    );
  } else if (isTaskCodeThieme(task)) {
    taskLabel = intlFmt(
      'appointmentSchedulingPortlet.appointmentDetail.fillThiemeQuestionnaire',
      intl(
        'appointmentSchedulingPortlet.appointmentDetail.fillThiemeQuestionnaireTitle'
      )
    );
  } else if (isTaskCodeBook(task)) {
    const firstInput = getTaskInputByType(task, 'SERVICE_TYPE');
    if (firstInput) {
      taskLabel = intlFmt(
        'appointmentSchedulingPortlet.appointmentDetail.book',
        ' "' +
          limitValueCharLengthIfNeeded(
            firstInput.value?.displayName,
            valueCharLimit
          ) +
          '"'
      );
    }
  } else if (isTaskCodeDonate(task)) {
    taskLabel = intlFmt(
      'appointmentSchedulingPortlet.appointmentDetail.donate',
      ' "' +
        limitValueCharLengthIfNeeded(
          task?.researchStudy?.title,
          valueCharLimit
        ) +
        '"'
    );
  } else {
    console.warn('Unknown task code', task?.code);
  }
  return taskLabel;
}

function limitValueCharLengthIfNeeded(value: string, length?: number) {
  if (!value || !length) {
    return value;
  }
  return value.slice(0, length);
}

function getTaskInputByType(
  task: Task,
  desiredType: SchedulingCode
): TaskInOutComponent | undefined {
  const desiredInput = task.input?.filter(input => {
    if (input.hasOwnProperty('valueCodeCategory')) {
      return (
        (input as TaskInputCodeValueComponent).valueCodeCategory === desiredType
      );
    }
    return false;
  });
  if (desiredInput?.length > 0) {
    return desiredInput[0];
  }
  if (task.input?.length > 0) {
    return task.input[0];
  }
  return undefined;
}

export type TaskDueDateInfo =
  | {
      text: string;
      isWarning: boolean;
      isPastDue: boolean;
    }
  | undefined;

export function useTaskDueDateInfo(task?: Task): TaskDueDateInfo {
  return useMemo(() => {
    if (!task?.dueDate || isTaskStatusCompleted(task.status)) {
      return undefined;
    }

    const currentDate = new Date();
    const currentTime = currentDate.getTime();
    currentDate.setHours(0, 0, 0, 0);
    const parsedDueDate = DefaultDateTimeFormatter.readStringDate(task.dueDate);
    const parsedDueDateTime = parsedDueDate.getTime();
    const numberOfDays = Math.floor(
      (parsedDueDate.getTime() - currentDate.getTime()) / (24 * 60 * 60 * 1000)
    );
    let text = '';
    let showWarning = false;
    let isPastDue = false;
    if (numberOfDays > 1) {
      if (numberOfDays < 4) {
        text = intlFmt('common.dueDate.in.days', numberOfDays);
        showWarning = true;
      } else {
        text = intlFmt('common.dueDate.on', getFormattedDate(parsedDueDate));
      }
    } else if (numberOfDays == 1) {
      text = intl('common.dueDate.tomorrow');
      showWarning = true;
    } else if (numberOfDays == 0 && currentTime < parsedDueDateTime) {
      text = intl('common.dueDate.today');
      showWarning = true;
    } else {
      text = intlFmt('common.dueDate.pastDue', getFormattedDate(parsedDueDate));
      showWarning = true;
      isPastDue = true;
    }

    return {
      text,
      isWarning: showWarning,
      isPastDue
    };
  }, [task]);
}

function getFormattedDate(parsedDueDate: Date): string {
  return customDateFormatter({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).formatDate(parsedDueDate);
}

export function getTasksLabels(tasks?: Task[]): string[] {
  const tasksLabels: string[] = [];
  tasks?.forEach(task => {
    const label = getTaskLabel(task);
    if (label.length > 0) {
      tasksLabels.push(label);
    }
  });
  return tasksLabels;
}

export function isTaskStatusCompleted(taskStatus?: string): boolean {
  return taskStatus === TaskStatusEnum.COMPLETED;
}

export const isTaskStatusInProgress = (taskStatus?: string) =>
  taskStatus === TaskStatusEnum.INPROGRESS;

export const isTaskStatusReady = (taskStatus?: string) =>
  taskStatus === TaskStatusEnum.READY;

export function isTaskStatusRejected(taskStatus?: string): boolean {
  return taskStatus === TaskStatusEnum.REJECTED;
}

export const isTaskStatusOpen = (task?: Task) =>
  isTaskStatusReady(task?.status) || isTaskStatusInProgress(task?.status);

export const getTaskStatusText = (task: Task): string => {
  if (isTaskStatusOpen(task)) {
    if (task?.optional) {
      return intl('common.task.status.optional');
    } else {
      return intl('common.task.status.todo');
    }
  } else if (isTaskStatusRejected(task?.status)) {
    return intl('common.task.status.rejected');
  } else {
    return intl('common.task.status.completed');
  }
};

export function isTaskCodeUpload(task?: Task): boolean {
  return (task?.code?.value?.toUpperCase() as TaskCodeType) === 'UPLOAD';
}

export function isTaskCodeQuestionnaire(task?: Task): boolean {
  return (task?.code?.value?.toUpperCase() as TaskCodeType) === 'QUESTIONNAIRE';
}

export function isTaskCodeRead(task?: Task): boolean {
  return (task?.code?.value?.toUpperCase() as TaskCodeType) === 'READ';
}

export function isTaskCodeThieme(task?: Task): boolean {
  return (task?.code?.value?.toUpperCase() as TaskCodeType) === 'THIEME';
}

export function isTaskCodeBook(task?: Task): boolean {
  return (task?.code?.value?.toUpperCase() as TaskCodeType) === 'BOOK';
}

export function isTaskCodeDonate(task?: Task): boolean {
  return (task?.code?.value?.toUpperCase() as TaskCodeType) === 'DONATE';
}
